import React, { useContext, useMemo, useState } from 'react'
import { Alert } from '@mui/material'

import InstructorDashboardPanel from '../../instructorDashboard/InstructorDashboardPanel'
import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { GroupsProvider } from '../../../context/GroupsContext'
import { SelectedGroupProvider } from '../../../context/SelectedGroupContext'
import { SelectedCodeProvider } from '../../../context/SelectedCodeContext'
import { ModeContextProvider } from '../../../context/ModeContext'
import { PresenterListProvider } from '../../../context/PresenterListContext'
import { TestResultContextProvider } from '../../../context/TestResultContext'
import { ErrorContextProvider } from '../../../context/ErrorContext'
import { AutoGroupingContextProvider } from '../../../context/AutoGroupingContext'
import { CurrentStepContextProvider } from '../../../context/CurrentStepContext'
import { MessageProvider } from '../../../context/MessageContext'

// One condition's Monitor (Session Control, Insights, Replay), exactly as on its own session
// page, inside the experiment page.
//
// Most of the Monitor follows its sessionId prop. Its Live Feed and Pairing views read the
// page-wide session instead (SessionContext, and currentUser.session_id for submissions),
// which on the experiment page is the experiment's own row - so they get this condition
// through scoped copies of those contexts, plus fresh copies of the page-level providers
// they share, so nothing they select or toggle leaks into another condition or the page.
// The page mounts one condition's Monitor at a time.
const ConditionMonitor = ({ condition, row, focusRequest, onFocusHandled }) => {
  const auth = useContext(AuthContext)
  const [link, setLink] = useState('')

  // The page re-reads every condition's row every few seconds, so a write made from inside
  // this Monitor shows up with the next read; its own setSession calls have nothing to do.
  const sessionValue = useMemo(
    () => ({ session: row, setSession: () => {}, sessionLoading: false, link, setLink }),
    [row, link]
  )
  // Memoized: SubmissionsProvider re-subscribes whenever currentUser changes.
  const authValue = useMemo(
    () => ({ ...auth, currentUser: auth.currentUser ? { ...auth.currentUser, session_id: row.id } : auth.currentUser }),
    [auth, row.id]
  )

  return (
    <AuthContext.Provider value={authValue}>
      <SessionContext.Provider value={sessionValue}>
        <GroupsProvider sessionId={row.id}>
          <SelectedGroupProvider>
            <SelectedCodeProvider>
              <ModeContextProvider>
                <PresenterListProvider>
                  <TestResultContextProvider>
                    <ErrorContextProvider>
                      <AutoGroupingContextProvider>
                        <CurrentStepContextProvider>
                          <MessageProvider>
                            <InstructorDashboardPanel
                              sessionId={row.id}
                              isActive
                              focusRequest={focusRequest}
                              onFocusHandled={onFocusHandled}
                              lobbyNotice={
                                <Alert severity="info">
                                  These controls start, pause and reset this condition alone (
                                  <strong>{condition.name}</strong>). Use the controls at the top of the page to run
                                  every condition together.
                                </Alert>
                              }
                            />
                          </MessageProvider>
                        </CurrentStepContextProvider>
                      </AutoGroupingContextProvider>
                    </ErrorContextProvider>
                  </TestResultContextProvider>
                </PresenterListProvider>
              </ModeContextProvider>
            </SelectedCodeProvider>
          </SelectedGroupProvider>
        </GroupsProvider>
      </SessionContext.Provider>
    </AuthContext.Provider>
  )
}

export default ConditionMonitor
